import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { LoadingButton } from '@mui/lab';
import { useSWRConfig } from 'swr';
import moment from 'moment';
import {
  AccountData,
  EditPassDTO,
  OrderData,
  OrderService,
  PassOut,
  PassService,
  PassStatus,
  RefundOwnerType,
} from '../../api';
import { isRecurringPassType, RecurringAction } from './utils';
import { ApiResponseHandlerType } from '../../common/apiNotifications/ApiResponseHandlerContext';
import { useApiResponseHandlerContext } from '../../common/apiNotifications/useApiResponseHandlerContext';
import { getPassOrderPage } from '../../utils/AccountUtils';
import { useAuth } from '../../session/InternalAuthProvider';
import TitledDrawer from '../../common/ui/TitledDrawer';
import ConfirmationDialog from '../../common/ui/ConfimationDialog';
import { RefundOfferDialog, RefundOfferTarget } from '../../refunds/RefundOfferDialog';
import { useLanguage } from '../../common/GeneralUtils';
import PauseScheduleDialog from './PauseScheduleDialog';
import { EditPassFormValues, PassDetailPanels } from './PassDetailTypes';
import { SummaryPanel } from './components/summaryPanel/SummaryPanel';
import { PunchesPanel } from './components/punchesPanel/PunchesPanel';
import { SchedulePanel } from './components/schedulePanel/SchedulePanel';
import { ChangeScheduleDialog } from './components/schedulePanel/ChangeScheduleDialog';
import { ReleaseScheduleDialog } from './components/schedulePanel/ReleaseScheduleDialog';
import { AdjustPunchesDialog } from './components/punchesPanel/AdjustPunchesDialog';
import { PersonalDetailsPanel } from './components/personalDetailsPanel/PersonalDetailsPanel';
import { ManagementPanel } from './components/managementPanel/ManagementPanel';
import { BillingPanel } from './components/billingPanel/BillingPanel';
import { ActivityLogPanel } from './components/activityLogPanel/ActivityLogPanel';
import { CommunicationTrackingPanel } from '../../calendar/dialogs/EditOrderDialog/components/communicationTrackingPanel/CommunicationTrackingPanel';

type PassDetailDrawerProps = {
  open: boolean;
  onClose: () => void;
  pass?: PassOut;
  onPassUpdated?: (pass: PassOut) => void;
  /** Panel to expand when the drawer opens, e.g. the table's subscription shortcut. */
  initialExpandedPanel?: PassDetailPanels;
};

const PassDetailDrawer = ({ open, onClose, pass, onPassUpdated, initialExpandedPanel }: PassDetailDrawerProps) => {
  const { t } = useTranslation();
  const { mutate } = useSWRConfig();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const [expandedPanel, setExpandedPanel] = useState<PassDetailPanels | undefined>(initialExpandedPanel);
  const [redeemedOrders, setRedeemedOrders] = useState<OrderData[]>([]);
  const [canceledOrderDetails, setCanceledOrderDetails] = useState<Map<string, OrderData>>(new Map());
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<OrderData | undefined>();
  const [deleteOrderDialogOpen, setDeleteOrderDialogOpen] = useState(false);
  const [pendingRecurringAction, setPendingRecurringAction] = useState<RecurringAction | null>(null);
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [changeScheduleOpen, setChangeScheduleOpen] = useState(false);
  const [releaseScheduleOpen, setReleaseScheduleOpen] = useState(false);
  const [adjustPunchesOpen, setAdjustPunchesOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [refundOffer, setRefundOffer] = useState<RefundOfferTarget | undefined>(undefined);
  const language = useLanguage();
  const { showResponseNotification } = useApiResponseHandlerContext();

  useEffect(() => {
    if (open) setExpandedPanel(initialExpandedPanel);
  }, [open, pass?.id, initialExpandedPanel]);

  useEffect(() => {
    let cancelled = false;
    const fetchOrders = async () => {
      if (!open) {
        setRedeemedOrders([]);
        setCanceledOrderDetails(new Map());
        return;
      }
      setOrdersLoading(true);
      const [redeemed, canceledResults] = await Promise.all([
        pass?.redeemed_order_ids?.length
          ? Promise.all(pass.redeemed_order_ids.map((id) => OrderService.getOrder(id)))
          : Promise.resolve([]),
        pass?.canceled_orders?.length
          ? Promise.allSettled(pass.canceled_orders.map((co) => OrderService.getOrder(co.order_id)))
          : Promise.resolve([]),
      ]);
      if (cancelled) return;
      setRedeemedOrders(redeemed);
      const detailsMap = new Map<string, OrderData>();
      canceledResults.forEach((result, i) => {
        if (result.status === 'fulfilled') {
          detailsMap.set(pass!.canceled_orders![i].order_id, result.value);
        }
      });
      setCanceledOrderDetails(detailsMap);
      setOrdersLoading(false);
    };
    void fetchOrders();
    return () => {
      cancelled = true;
    };
  }, [open, pass?.id, pass?.redeemed_order_ids, pass?.canceled_orders]);

  if (!pass) return null;

  const isRecurring = isRecurringPassType(getPassOrderPage(account, pass.order_page_id)?.pass_type);
  const totalPunches = pass.num_punches_override ?? getPassOrderPage(account, pass.order_page_id)?.num_punches ?? null;

  const handlePanelChange = (panel: PassDetailPanels) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPanel(isExpanded ? panel : undefined);
  };

  const validationSchema = yup.object().shape({
    firstname: yup.string().required(t('editOrderPage.yup.required')),
    lastname: yup.string().required(t('editOrderPage.yup.required')),
    email: yup.string().email().required(t('editOrderPage.yup.required')),
    phone: yup.string().required(t('editOrderPage.yup.required')),
    price: yup.number().min(0).required(t('editOrderPage.yup.required')),
  });

  const initialValues: EditPassFormValues = {
    firstname: pass.firstname,
    lastname: pass.lastname,
    alternative_full_name: pass.alternative_full_name || '',
    email: pass.email,
    phone: pass.phone,
    price: pass.price,
    expiration_date: pass.expiration_date ? moment(pass.expiration_date).format('YYYY-MM-DD') : '',
    start_date: pass.start_date ? moment(pass.start_date).format('YYYY-MM-DD') : '',
    comment: pass.comment || '',
    permanent_schedule_hold: pass.permanent_schedule_hold ?? false,
  };

  const handleSubmit = async (values: EditPassFormValues) => {
    const dto: EditPassDTO = {
      pass_id: pass.id,
      firstname: values.firstname,
      lastname: values.lastname,
      alternative_full_name: values.alternative_full_name || undefined,
      email: values.email,
      phone: values.phone,
      price: values.price,
      expiration_date: values.expiration_date || undefined,
      start_date: values.start_date || undefined,
      comment: values.comment || undefined,
      permanent_schedule_hold: values.permanent_schedule_hold,
    };
    try {
      setSubmitError(null);
      const updatedPass = await PassService.editPass(dto);
      await mutate((key: any) => key?.opId === 'getPasses');
      onPassUpdated?.(updatedPass);
      onClose();
    } catch (e) {
      setSubmitError(t('saveHeader.errorGeneric'));
    }
  };

  const mapErrorKey = (e: unknown): string => {
    const status = (e as { status?: number })?.status;
    if (status === 400) return 'passes.recurring.errors.invalidState';
    if (status === 402) return 'passes.recurring.errors.chargeDeclined';
    if (status === 404) return 'passes.recurring.errors.notFound';
    if (status === 500) return 'passes.recurring.errors.notConfigured';
    return 'saveHeader.errorGeneric';
  };

  const afterRecurringUpdate = async (updated: PassOut) => {
    onPassUpdated?.(updated);
    await mutate((key: any) => key?.opId === 'getPasses');
  };

  const executeRecurringAction = async () => {
    if (!pendingRecurringAction) return;
    try {
      let updated: PassOut;
      let toastKey: string;
      switch (pendingRecurringAction) {
        case RecurringAction.Resume:
          updated = await PassService.resumePass(pass.id);
          toastKey = 'passes.recurring.resumed';
          break;
        case RecurringAction.CancelPause:
          updated = await PassService.resumePass(pass.id);
          toastKey = 'passes.recurring.pauseCanceled';
          break;
        case RecurringAction.Reactivate:
          updated = await PassService.reactivatePass(pass.id);
          toastKey = 'passes.recurring.reactivated';
          break;
        case RecurringAction.Cancel:
        default:
          updated = await PassService.archivePass(pass.id);
          toastKey = 'passes.recurring.canceled';
          break;
      }
      await afterRecurringUpdate(updated);
      showResponseNotification(t(toastKey), ApiResponseHandlerType.SUCCESS);
      if (pendingRecurringAction === RecurringAction.Cancel) {
        setRefundOffer({
          ownerType: RefundOwnerType.PASS,
          ownerId: pass.id,
          title: t('refunds.offer.passTitle'),
          customerName: `${pass.firstname} ${pass.lastname}`,
          ownerLabel: getPassOrderPage(account, pass.order_page_id)?.label[language] ?? t('refunds.entity.pass'),
        });
      }
    } catch (e) {
      showResponseNotification(t(mapErrorKey(e)), ApiResponseHandlerType.ERROR);
    }
  };

  const executePause = async (dates: { pause_start_date: string; pause_end_date: string }) => {
    const isEdit = !!pass.pause_start_date || !!pass.pause_end_date;
    try {
      const updated = await PassService.pausePass(pass.id, dates);
      await afterRecurringUpdate(updated);
      showResponseNotification(
        t(isEdit ? 'passes.recurring.pauseUpdated' : 'passes.recurring.paused'),
        ApiResponseHandlerType.SUCCESS,
      );
    } catch (e) {
      showResponseNotification(t(mapErrorKey(e)), ApiResponseHandlerType.ERROR);
    }
  };

  const recurringDialogTitle = pendingRecurringAction ? t(`passes.recurring.${pendingRecurringAction}`) : '';
  const recurringDialogContent = pendingRecurringAction
    ? t(`passes.recurring.${pendingRecurringAction}Confirm`, {
        date: pass.expiration_date ? moment(pass.expiration_date).format('ll') : '',
      })
    : '';

  const title = `${t('passes.pass')} - #${pass.order_number}`;

  return (
    <TitledDrawer
      title={title}
      open={open}
      onClose={onClose}
      openAll={() => setExpandedPanel(PassDetailPanels.AllOpen)}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize>
        {({ isSubmitting, dirty, errors, submitCount }) => {
          const firstValidationError = submitCount > 0 ? (Object.values(errors)[0] as string | undefined) : undefined;
          const displayedError = submitError ?? firstValidationError;
          return (
            <Form autoComplete="on" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Grid
                container
                flexDirection="column"
                flexWrap="nowrap"
                sx={{ pt: { xs: '12px', md: '18px' }, paddingInline: { xs: '12px', md: '24px' }, pb: 0 }}
                overflow="hidden"
                height="100%"
                flex={1}>
                <Grid
                  container
                  flexDirection="column"
                  gap={1.5}
                  sx={{
                    overflowY: 'auto',
                    flex: 1,
                    minHeight: 0,
                    width: 'auto',
                    marginInline: '-4px',
                    paddingInline: '4px',
                    paddingBlockStart: '4px',
                    marginBlockStart: '-4px',
                    paddingBlockEnd: '12px',
                    '& > .MuiAccordion-root + .MuiAccordion-root, & > .MuiAccordion-root + .MuiAccordion-root.Mui-expanded':
                      { marginTop: 0 },
                  }}
                  flexWrap="nowrap">
                  <SummaryPanel pass={pass} expandedPanel={expandedPanel} onPanelChange={handlePanelChange} />

                  {isRecurring && (
                    <BillingPanel
                      pass={pass}
                      expandedPanel={expandedPanel}
                      onPanelChange={handlePanelChange}
                      onUpdated={afterRecurringUpdate}
                      onRecurringAction={setPendingRecurringAction}
                      onPauseRequest={() => setPauseDialogOpen(true)}
                    />
                  )}

                  <PunchesPanel
                    pass={pass}
                    expandedPanel={expandedPanel}
                    onPanelChange={handlePanelChange}
                    redeemedOrders={redeemedOrders}
                    canceledOrderDetails={canceledOrderDetails}
                    ordersLoading={ordersLoading}
                    onDeleteOrder={(order) => {
                      setOrderToDelete(order);
                      setDeleteOrderDialogOpen(true);
                    }}
                    onAdjustPunches={() => setAdjustPunchesOpen(true)}
                  />

                  <SchedulePanel
                    pass={pass}
                    expandedPanel={expandedPanel}
                    onPanelChange={handlePanelChange}
                    onChangeScheduleClick={() => setChangeScheduleOpen(true)}
                    onReleaseScheduleClick={() => setReleaseScheduleOpen(true)}
                  />

                  <PersonalDetailsPanel pass={pass} expandedPanel={expandedPanel} onPanelChange={handlePanelChange} />

                  <ManagementPanel
                    pass={pass}
                    expandedPanel={expandedPanel}
                    isRecurring={isRecurring}
                    onPanelChange={handlePanelChange}
                  />

                  <CommunicationTrackingPanel
                    expanded={expandedPanel}
                    handleChange={handlePanelChange}
                    passId={pass.id}
                    panelName={PassDetailPanels.CommunicationTracking}
                    allOpenValue={PassDetailPanels.AllOpen}
                  />

                  <ActivityLogPanel pass={pass} expandedPanel={expandedPanel} onPanelChange={handlePanelChange} />
                </Grid>
              </Grid>

              <Grid
                container
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  py: 1.5,
                  paddingInline: { xs: '12px', md: '24px' },
                  borderTop: '1px solid',
                  borderColor: 'mb.lineSoft',
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginInlineStart: 'auto' }}>
                  {displayedError && (
                    <Typography variant="caption" color="error">
                      {displayedError}
                    </Typography>
                  )}
                  <LoadingButton loading={isSubmitting} type="submit" variant="contained" disabled={!dirty}>
                    {t('save')}
                  </LoadingButton>
                </Box>
              </Grid>
              <ConfirmationDialog
                title={t('calendar.delete_order.title')}
                content={<Typography>{t('calendar.delete_order.description')}</Typography>}
                isOpen={deleteOrderDialogOpen}
                onClose={async (actionConfirmed) => {
                  if (actionConfirmed && orderToDelete) {
                    await OrderService.deleteOrder(orderToDelete.id);
                    setRedeemedOrders((prev) => prev.filter((o) => o.id !== orderToDelete.id));
                    const refreshedPass = await PassService.getPassById(pass.id);
                    onPassUpdated?.(refreshedPass);
                  }
                  setDeleteOrderDialogOpen(false);
                }}
              />
              <ConfirmationDialog
                title={recurringDialogTitle}
                content={<Typography>{recurringDialogContent}</Typography>}
                ctaText={recurringDialogTitle}
                confirmButtonColor={pendingRecurringAction === RecurringAction.Cancel ? 'warning' : 'primary'}
                isOpen={pendingRecurringAction !== null}
                onClose={async (actionConfirmed) => {
                  if (actionConfirmed) {
                    await executeRecurringAction();
                  }
                  setPendingRecurringAction(null);
                }}
              />
              {refundOffer && <RefundOfferDialog isOpen onClose={() => setRefundOffer(undefined)} {...refundOffer} />}
              <PauseScheduleDialog
                open={pauseDialogOpen}
                onClose={() => setPauseDialogOpen(false)}
                onSubmit={executePause}
                initialStart={pass.pause_start_date}
                initialEnd={pass.pause_end_date}
                lockStart={pass.status === PassStatus.SUSPENDED && !!pass.pause_start_date}
              />
              {getPassOrderPage(account, pass.order_page_id) && (
                <ChangeScheduleDialog
                  open={changeScheduleOpen}
                  onClose={() => setChangeScheduleOpen(false)}
                  pass={pass}
                  passOrderPage={getPassOrderPage(account, pass.order_page_id)!}
                  onUpdated={afterRecurringUpdate}
                />
              )}
              <ReleaseScheduleDialog
                open={releaseScheduleOpen}
                onClose={() => setReleaseScheduleOpen(false)}
                pass={pass}
                onUpdated={afterRecurringUpdate}
              />
              {totalPunches != null && (
                <AdjustPunchesDialog
                  open={adjustPunchesOpen}
                  onClose={() => setAdjustPunchesOpen(false)}
                  pass={pass}
                  totalPunches={totalPunches}
                  onUpdated={afterRecurringUpdate}
                />
              )}
            </Form>
          );
        }}
      </Formik>
    </TitledDrawer>
  );
};

export default PassDetailDrawer;
